// src/lib/dashboard/kpisAutomaticos.ts
//
// Catálogo de KPIs que o sistema JÁ sabe calcular, três por departamento mais
// alternativas. Saem prontos na aba CEO, com o número de verdade, a comparação
// com o período anterior e uma meta sugerida — o gestor não começa de uma tela
// vazia, e o Igor valida com um clique (ou troca por uma alternativa).
//
// Cada cálculo reaproveita a regra que a tela de detalhe usa (dinheiro por
// vencimento, faturamento por usina, funil do CRM, mídia paga), para o número
// do KPI nunca divergir do número da aba correspondente.
//
// Função pura: roda sob `node --test` com type-stripping.

import type { FaturaPainel } from '../financeiro/types.ts';
import type { UnidadeKpi } from './kpisDepartamento.ts';
import { ehCobranca, ehContaInterna, estaPago, diasEmissaoAtePagamento } from '../financeiro/definicoesKpi.ts';
import { calcularDinheiroDoPeriodo, mesDoOrdinal, ordinalDoMes, ultimoMesFechado } from './painelCeo.ts';

export interface NumerosComercial { prospeccoes: number; assinados: number; aprovados: number; kwhAprovados: number }
export interface NumerosMarketing { investimento: number; leads: number; resultados: number }
export interface NumerosChamados {
  resolvidos: number;
  /** Dias da abertura à conclusão, em média, nos chamados concluídos no mês. */
  diasMedios: number | null;
  /** O mesmo tempo, pela mediana: um chamado de 60 dias não esconde que a maioria sai em 1. */
  diasMediana: number | null;
  /** Fração dos concluídos no mês que levaram até `PRAZO_CHAMADO_DIAS`. */
  noPrazo: number | null;
  /** Dias do mês em que o time entregou pelo menos uma solicitação. */
  diasAtivos: number;
}

/** Dias úteis (seg–sex) do mês da data: a meta natural de "dias ativos". */
export function diasUteisDoMes(hoje: Date): number {
  const ultimo = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
  let n = 0;
  for (let d = 1; d <= ultimo; d++) {
    const dia = new Date(hoje.getFullYear(), hoje.getMonth(), d).getDay();
    if (dia !== 0 && dia !== 6) n++;
  }
  return n;
}

/** Prazo do KPI "resolvidos em até N dias". */
export const PRAZO_CHAMADO_DIAS = 3;

/** Tudo que os cálculos leem. `null` num campo = aquele dado ainda está carregando. */
export interface ContextoKpis {
  hoje: Date;
  faturas: FaturaPainel[] | null;
  parteVigor: ((f: FaturaPainel) => number) | null;
  /** Soma das metas de faturamento das usinas no mês fechado (0 = ninguém definiu). */
  metaFaturamentoUsinas: number;
  comercial: { atual: NumerosComercial; anterior: NumerosComercial } | null;
  marketing: { atual: NumerosMarketing; anterior: NumerosMarketing } | null;
  geracao: { atual: number | null; anterior: number | null } | null;
  paradas: { horas: number; horasComMotivo: number } | null;
  energia: { usoAtual: number | null; usoAnterior: number | null } | null;
  chamados: { atual: NumerosChamados; anterior: NumerosChamados } | null;
}

export interface ValorKpi {
  atual: number | null;
  anterior: number | null;
  /** "OUT/2026 até hoje", "SET/2026", "12 meses". */
  periodo: string;
  /** Rótulo do período anterior, para "vs SET". */
  rotuloAnterior: string;
  /**
   * Quanto do período já passou (0 a 1). Contagem do mês corrente vem com a fração
   * de dias corridos: no dia 19 de 31, 10 contratos não são "68% pior que setembro"
   * — compara-se com setembro e com a meta no mesmo ponto do mês.
   */
  fracao?: number;
}

export interface DefinicaoKpiAuto {
  id: string;
  departamento: string;
  nome: string;
  como_medir: string;
  unidade: UnidadeKpi;
  sentido: 'maior' | 'menor';
  /** Os três primeiros de cada departamento são os sugeridos; os demais, alternativas. */
  calcular: (ctx: ContextoKpis) => ValorKpi | null;
  metaSugerida: (v: ValorKpi, ctx: ContextoKpis) => number;
  /** O valor sai de outra tela (ex.: ferramentas na Visão Geral): a aba Metas mostra "—", não "carregando". */
  semValorNoPainel?: boolean;
}

// ─────────────── apoio ───────────────

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const curto = (mesRef: string) => mesRef.slice(0, 3);

/** Arredonda para um número "de meta": 1.234 → 1.300, 87,4 → 90. */
export function numeroRedondo(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  const ordem = 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1);
  return Math.ceil(v / ordem) * ordem;
}

/** Meta de crescimento: 10% acima do melhor entre o mês atual e o anterior. */
const crescer = (v: ValorKpi) => numeroRedondo(Math.max(v.atual ?? 0, v.anterior ?? 0) * 1.1);

function mesFechado(ctx: ContextoKpis) {
  const atual = ultimoMesFechado(ctx.faturas ?? [], ctx.hoje);
  const anterior = mesDoOrdinal((ordinalDoMes(atual) ?? 0) - 1);
  return { atual, anterior };
}

function doMes(ctx: ContextoKpis, mes: string) {
  return (ctx.faturas ?? []).filter((f) => f.mes_referencia === mes && !ehContaInterna(f));
}

/** Janela de um mês do calendário a partir do ordinal: [1º dia, último dia]. */
function janelaDoOrdinal(ord: number): [string, string] {
  const ano = Math.floor(ord / 12);
  const m = ord % 12;
  return [isoLocal(new Date(ano, m, 1)), isoLocal(new Date(ano, m + 1, 0))];
}

/** 12 meses até hoje (`anterior` = false) ou até o fim do mês passado (true). */
function inadimplencia12(ctx: ContextoKpis, anterior: boolean) {
  const h = ctx.hoje;
  const fim = anterior ? new Date(h.getFullYear(), h.getMonth(), 0) : h;
  const ini = new Date(fim.getFullYear(), fim.getMonth() - 11, 1);
  return calcularDinheiroDoPeriodo(ctx.faturas ?? [], isoLocal(ini), isoLocal(fim), ctx.hoje, null);
}

// ── Inadimplência pela regra do plano de bônus ──
// Decisão de 27/10/2026: a tela inicial inteira usa esta regra, não a de 12 meses.

/**
 * Boletos que já tiveram 30 dias de cobrança: vencimento entre 3 meses e 31 dias
 * atrás. No dia 27/10, de 27/06 a 26/09.
 */
export function janelaInadimplenciaBonus(hoje: Date): { inicio: string; fim: string } {
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 31);
  const inicio = new Date(fim.getFullYear(), fim.getMonth() - 3, fim.getDate() + 1);
  return { inicio: isoLocal(inicio), fim: isoLocal(fim) };
}

export interface InadimplenciaBonus {
  /** Valor vencido há mais de 30 dias e ainda em aberto ÷ valor desses boletos (0 a 1). */
  taxa: number | null;
  vencido: number;
  emitido: number;
  /** Todo o vencido em aberto, de qualquer época. */
  estoque: number;
  janela: { inicio: string; fim: string };
}

export function inadimplenciaRegraBonus(faturas: FaturaPainel[], hoje: Date): InadimplenciaBonus {
  const janela = janelaInadimplenciaBonus(hoje);
  const d = calcularDinheiroDoPeriodo(faturas, janela.inicio, janela.fim, hoje, null);
  const estoque = calcularDinheiroDoPeriodo(faturas, null, isoLocal(hoje), hoje, null).vencido;
  return { taxa: d.inadimplencia, vencido: d.vencido, emitido: d.recebido + d.vencido, estoque, janela };
}

/** Meta do plano de bônus para a inadimplência acima, em out–dez/2026. */
export const META_INADIMPLENCIA_BONUS = 13;
/** Meta do plano de bônus para o prazo médio de recebimento. */
export const META_PRAZO_RECEBIMENTO_DIAS = 14;

const MES_ATUAL = (ctx: ContextoKpis) => `${mesDoOrdinal(ctx.hoje.getFullYear() * 12 + ctx.hoje.getMonth())} até hoje`;

/** Fração do mês corrente que já passou: dia 19 de 31 → 0,61. */
export const fracaoDoMes = (hoje: Date) => hoje.getDate() / new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
const MES_PASSADO = (ctx: ContextoKpis) => curto(mesDoOrdinal(ctx.hoje.getFullYear() * 12 + ctx.hoje.getMonth() - 1));

// ─────────────── catálogo ───────────────

export const KPIS_AUTOMATICOS: DefinicaoKpiAuto[] = [
  // ── Comercial ──
  {
    id: 'com_aprovados', departamento: 'comercial', nome: 'Contratos aprovados', unidade: 'numero', sentido: 'maior',
    como_medir: 'Negociações que chegaram a "termo aprovado" no mês, pelo funil do CRM.',
    calcular: (c) => c.comercial && { atual: c.comercial.atual.aprovados, anterior: c.comercial.anterior.aprovados, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },
  {
    id: 'com_kwh', departamento: 'comercial', nome: 'Energia em contratos aprovados', unidade: 'kwh', sentido: 'maior',
    como_medir: 'Soma do consumo (kWh/mês) das negociações aprovadas no mês.',
    calcular: (c) => c.comercial && { atual: c.comercial.atual.kwhAprovados, anterior: c.comercial.anterior.kwhAprovados, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },
  {
    id: 'com_conversao', departamento: 'comercial', nome: 'Conversão do funil', unidade: 'percentual', sentido: 'maior',
    como_medir: 'Contratos aprovados ÷ prospecções do mês.',
    calcular: (c) => c.comercial && {
      atual: c.comercial.atual.prospeccoes ? (c.comercial.atual.aprovados / c.comercial.atual.prospeccoes) * 100 : null,
      anterior: c.comercial.anterior.prospeccoes ? (c.comercial.anterior.aprovados / c.comercial.anterior.prospeccoes) * 100 : null,
      periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c),
    },
    metaSugerida: (v) => Math.max(2, Math.ceil(Math.max(v.atual ?? 0, v.anterior ?? 0) * 1.2)),
  },
  {
    id: 'com_assinados', departamento: 'comercial', nome: 'Termos assinados', unidade: 'numero', sentido: 'maior',
    como_medir: 'Negociações que chegaram a "termo assinado" no mês.',
    calcular: (c) => c.comercial && { atual: c.comercial.atual.assinados, anterior: c.comercial.anterior.assinados, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },

  // ── Financeiro ──
  {
    id: 'fin_inadimplencia', departamento: 'financeiro', nome: 'Inadimplência em 12 meses', unidade: 'percentual', sentido: 'menor',
    como_medir: 'Dos boletos que venceram nos últimos 12 meses, quanto não foi pago (R$ vencido ÷ R$ que já venceu).',
    calcular: (c) => {
      if (!c.faturas) return null;
      const a = inadimplencia12(c, false).inadimplencia;
      const b = inadimplencia12(c, true).inadimplencia;
      return { atual: a == null ? null : a * 100, anterior: b == null ? null : b * 100, periodo: '12 meses', rotuloAnterior: 'mês passado' };
    },
    metaSugerida: () => 10,
  },
  {
    id: 'fin_recebimento', departamento: 'financeiro', nome: 'Recebimento do mês passado', unidade: 'percentual', sentido: 'maior',
    como_medir: 'Dos boletos que venceram no mês passado, quanto do valor já entrou.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const ord = c.hoje.getFullYear() * 12 + c.hoje.getMonth();
      const taxa = (o: number) => {
        const [i, f] = janelaDoOrdinal(o);
        const d = calcularDinheiroDoPeriodo(c.faturas!, i, f, c.hoje, null);
        return d.esperado > 0 ? (d.recebido / d.esperado) * 100 : null;
      };
      return { atual: taxa(ord - 1), anterior: taxa(ord - 2), periodo: MES_PASSADO(c), rotuloAnterior: curto(mesDoOrdinal(ord - 2)) };
    },
    metaSugerida: () => 90,
  },
  {
    id: 'fin_vencido', departamento: 'financeiro', nome: 'Valor vencido sem pagar', unidade: 'reais', sentido: 'menor',
    como_medir: 'R$ dos boletos que venceram nos últimos 12 meses e continuam sem pagamento.',
    calcular: (c) => c.faturas && {
      atual: inadimplencia12(c, false).vencido, anterior: inadimplencia12(c, true).vencido, periodo: '12 meses', rotuloAnterior: 'mês passado',
    },
    metaSugerida: (v) => numeroRedondo((v.atual ?? 0) * 0.8),
  },
  {
    id: 'fin_prazo', departamento: 'financeiro', nome: 'Prazo médio de recebimento', unidade: 'dias', sentido: 'menor',
    como_medir: 'Dias da emissão do boleto até o pagamento, nas contas do último mês fechado.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const { atual, anterior } = mesFechado(c);
      const media = (mes: string) => {
        const dias = doMes(c, mes).filter(estaPago).map(diasEmissaoAtePagamento).filter((d): d is number => d != null);
        return dias.length ? dias.reduce((t, d) => t + d, 0) / dias.length : null;
      };
      return { atual: media(atual), anterior: media(anterior), periodo: atual, rotuloAnterior: curto(anterior) };
    },
    metaSugerida: (v) => Math.max(5, Math.floor((v.atual ?? 20) * 0.9)),
  },
  {
    // Alternativa no fim da lista: não muda as três sugestões da aba CEO. É a régua da tela
    // inicial (Diretoria e Gestão do Financeiro) e do plano de bônus.
    id: 'fin_inadimplencia_bonus', departamento: 'financeiro', nome: 'Inadimplência (regra do bônus)', unidade: 'percentual', sentido: 'menor',
    como_medir: 'Valor vencido há mais de 30 dias e ainda em aberto ÷ valor desses boletos, nos 3 meses anteriores (regra do plano de bônus). Foto do dia.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const r = inadimplenciaRegraBonus(c.faturas, c.hoje);
      return { atual: r.taxa == null ? null : r.taxa * 100, anterior: null, periodo: 'hoje', rotuloAnterior: '' };
    },
    metaSugerida: () => META_INADIMPLENCIA_BONUS,
  },

  // ── Gestão de clientes (faturamento) ──
  {
    id: 'ges_faturado', departamento: 'gestao', nome: 'Faturamento do mês', unidade: 'reais', sentido: 'maior',
    como_medir: 'Parte da Vigor nas cobranças do último mês fechado (mesma conta do Faturamento por usina).',
    calcular: (c) => {
      if (!c.faturas || !c.parteVigor) return null;
      const { atual, anterior } = mesFechado(c);
      const soma = (mes: string) => doMes(c, mes).filter(ehCobranca).reduce((t, f) => t + c.parteVigor!(f), 0);
      return { atual: soma(atual), anterior: soma(anterior), periodo: atual, rotuloAnterior: curto(anterior) };
    },
    metaSugerida: (v, c) => (c.metaFaturamentoUsinas > 0 ? c.metaFaturamentoUsinas : numeroRedondo(Math.max(v.atual ?? 0, v.anterior ?? 0) * 1.05)),
  },
  {
    id: 'ges_boletos', departamento: 'gestao', nome: 'Boletos emitidos', unidade: 'numero', sentido: 'maior',
    como_medir: 'Contas do último mês fechado que viraram cobrança.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const { atual, anterior } = mesFechado(c);
      return { atual: doMes(c, atual).filter(ehCobranca).length, anterior: doMes(c, anterior).filter(ehCobranca).length, periodo: atual, rotuloAnterior: curto(anterior) };
    },
    metaSugerida: crescer,
  },
  {
    id: 'ges_clientes', departamento: 'gestao', nome: 'Clientes faturados', unidade: 'numero', sentido: 'maior',
    como_medir: 'UCs diferentes com cobrança no último mês fechado.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const { atual, anterior } = mesFechado(c);
      const ucs = (mes: string) => new Set(doMes(c, mes).filter(ehCobranca).map((f) => f.uc_codigo).filter(Boolean)).size;
      return { atual: ucs(atual), anterior: ucs(anterior), periodo: atual, rotuloAnterior: curto(anterior) };
    },
    metaSugerida: crescer,
  },
  {
    id: 'ges_sem_cobranca', departamento: 'gestao', nome: 'Contas que não viraram cobrança', unidade: 'numero', sentido: 'menor',
    como_medir: 'Contas do último mês fechado marcadas "sem cobrança" — energia entregue que não foi cobrada.',
    calcular: (c) => {
      if (!c.faturas) return null;
      const { atual, anterior } = mesFechado(c);
      const n = (mes: string) => doMes(c, mes).filter((f) => f.statusDerivado === 'Sem cobrança').length;
      return { atual: n(atual), anterior: n(anterior), periodo: atual, rotuloAnterior: curto(anterior) };
    },
    metaSugerida: (v) => Math.floor((v.atual ?? 0) * 0.5),
  },

  // ── Geradoras ──
  {
    id: 'ger_meta', departamento: 'geradoras', nome: 'Geração x meta', unidade: 'percentual', sentido: 'maior',
    como_medir: 'Energia gerada ÷ meta do laudo (usinas sem laudo usam a produtividade de Vera Cruz), até hoje.',
    calcular: (c) => c.geracao && {
      atual: c.geracao.atual == null ? null : c.geracao.atual * 100,
      anterior: c.geracao.anterior == null ? null : c.geracao.anterior * 100,
      periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c),
    },
    metaSugerida: () => 95,
  },
  {
    id: 'ger_horas', departamento: 'geradoras', nome: 'Horas de usina parada', unidade: 'horas', sentido: 'menor',
    como_medir: 'Soma das horas sem gerar de todas as usinas no mês (parada e operação parcial em horas equivalentes).',
    calcular: (c) => c.paradas && { atual: c.paradas.horas, anterior: null, periodo: MES_ATUAL(c), rotuloAnterior: '', fracao: fracaoDoMes(c.hoje) },
    metaSugerida: () => 24,
  },
  {
    id: 'ger_motivos', departamento: 'geradoras', nome: 'Paradas com motivo registrado', unidade: 'percentual', sentido: 'maior',
    como_medir: 'Das horas paradas no mês, quanto tem o motivo registrado pela equipe em Geradoras › Análise.',
    calcular: (c) => c.paradas && {
      atual: c.paradas.horas > 0 ? (c.paradas.horasComMotivo / c.paradas.horas) * 100 : null,
      anterior: null, periodo: MES_ATUAL(c), rotuloAnterior: '',
    },
    metaSugerida: () => 100,
  },
  {
    id: 'ger_uso', departamento: 'geradoras', nome: 'Energia usada pelos clientes', unidade: 'percentual', sentido: 'maior',
    como_medir: 'Do que as usinas produziram no último mês fechado, quanto os clientes do rateio compensaram.',
    calcular: (c) => c.energia && c.faturas && (() => {
      const { atual, anterior } = mesFechado(c);
      return {
        atual: c.energia.usoAtual == null ? null : c.energia.usoAtual * 100,
        anterior: c.energia.usoAnterior == null ? null : c.energia.usoAnterior * 100,
        periodo: atual, rotuloAnterior: curto(anterior),
      };
    })(),
    metaSugerida: () => 90,
  },

  // ── Marketing ──
  {
    id: 'mkt_leads', departamento: 'marketing', nome: 'Leads no mês', unidade: 'numero', sentido: 'maior',
    como_medir: 'Cadastros gerados pela mídia paga (Meta Ads) no mês.',
    calcular: (c) => c.marketing && { atual: c.marketing.atual.leads, anterior: c.marketing.anterior.leads, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },
  {
    id: 'mkt_cpl', departamento: 'marketing', nome: 'Custo por lead', unidade: 'reais', sentido: 'menor',
    como_medir: 'Investimento em mídia ÷ leads do mês.',
    calcular: (c) => c.marketing && {
      atual: c.marketing.atual.leads ? c.marketing.atual.investimento / c.marketing.atual.leads : null,
      anterior: c.marketing.anterior.leads ? c.marketing.anterior.investimento / c.marketing.anterior.leads : null,
      periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c),
    },
    metaSugerida: (v) => Math.max(1, Math.floor(Math.min(v.atual ?? Infinity, v.anterior ?? Infinity) * 0.9) || 1),
  },
  {
    id: 'mkt_resultados', departamento: 'marketing', nome: 'Conversas e resultados', unidade: 'numero', sentido: 'maior',
    como_medir: 'Resultados das campanhas no mês (conversas iniciadas, cadastros), como o Meta Ads conta.',
    calcular: (c) => c.marketing && { atual: c.marketing.atual.resultados, anterior: c.marketing.anterior.resultados, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },
  {
    id: 'mkt_custo_resultado', departamento: 'marketing', nome: 'Custo por resultado', unidade: 'reais', sentido: 'menor',
    como_medir: 'Investimento em mídia ÷ resultados do mês.',
    calcular: (c) => c.marketing && {
      atual: c.marketing.atual.resultados ? c.marketing.atual.investimento / c.marketing.atual.resultados : null,
      anterior: c.marketing.anterior.resultados ? c.marketing.anterior.investimento / c.marketing.anterior.resultados : null,
      periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c),
    },
    metaSugerida: (v) => Math.max(1, Math.floor(Math.min(v.atual ?? Infinity, v.anterior ?? Infinity) * 0.9) || 1),
  },

  // Verba de mídia (#6509): a meta do mês, na aba Metas, é a verba aprovada. Fica no fim
  // do bloco para não entrar entre os três sugeridos; a aba Metas sempre a mostra (fixo).
  {
    id: 'mkt_investimento', departamento: 'marketing', nome: 'Investimento em mídia (verba)', unidade: 'reais', sentido: 'menor',
    como_medir: 'Gasto no Meta Ads no mês. A meta de cada mês é a verba de mídia aprovada.',
    calcular: (c) => c.marketing && { atual: c.marketing.atual.investimento, anterior: c.marketing.anterior.investimento, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: (v) => numeroRedondo(Math.max(v.atual ?? 0, v.anterior ?? 0)),
  },

  // ── Tecnologia ──
  // Os três de Tecnologia foram escolhidos pelo usuário em 19/10/2026: dias ativos,
  // solicitações concluídas e tempo médio de entrega. Os outros ficam como alternativa.
  {
    id: 'tec_dias_ativos', departamento: 'tecnologia', nome: 'Dias ativos', unidade: 'dias', sentido: 'maior',
    como_medir: 'Dias do mês em que o time de Tecnologia entregou (concluiu) pelo menos uma solicitação da Central.',
    calcular: (c) => c.chamados && { atual: c.chamados.atual.diasAtivos, anterior: c.chamados.anterior.diasAtivos, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: (_v, c) => diasUteisDoMes(c.hoje),
  },
  {
    id: 'tec_resolvidos', departamento: 'tecnologia', nome: 'Solicitações concluídas', unidade: 'numero', sentido: 'maior',
    como_medir: 'Solicitações para Tecnologia concluídas no mês (Central de Solicitações).',
    calcular: (c) => c.chamados && { atual: c.chamados.atual.resolvidos, anterior: c.chamados.anterior.resolvidos, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c), fracao: fracaoDoMes(c.hoje) },
    metaSugerida: crescer,
  },
  {
    id: 'tec_tempo', departamento: 'tecnologia', nome: 'Tempo médio de entrega', unidade: 'dias', sentido: 'menor',
    como_medir: 'Dias da abertura até a conclusão, em média, nas solicitações de Tecnologia concluídas no mês.',
    calcular: (c) => c.chamados && { atual: c.chamados.atual.diasMedios, anterior: c.chamados.anterior.diasMedios, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c) },
    metaSugerida: (v) => Math.max(1, Math.floor((v.atual ?? 5) * 0.8)),
  },
  {
    id: 'tec_no_prazo', departamento: 'tecnologia', nome: `Chamados resolvidos em até ${PRAZO_CHAMADO_DIAS} dias`, unidade: 'percentual', sentido: 'maior',
    como_medir: `Dos chamados de Tecnologia concluídos no mês, quantos levaram até ${PRAZO_CHAMADO_DIAS} dias da abertura à conclusão.`,
    calcular: (c) => c.chamados && {
      atual: c.chamados.atual.noPrazo == null ? null : c.chamados.atual.noPrazo * 100,
      anterior: c.chamados.anterior.noPrazo == null ? null : c.chamados.anterior.noPrazo * 100,
      periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c),
    },
    metaSugerida: () => 80,
  },
  {
    id: 'tec_mediana', departamento: 'tecnologia', nome: 'Tempo típico para resolver', unidade: 'dias', sentido: 'menor',
    como_medir: 'Mediana dos dias da abertura à conclusão, nos chamados de Tecnologia concluídos no mês: metade sai mais rápido que isso.',
    calcular: (c) => c.chamados && { atual: c.chamados.atual.diasMediana, anterior: c.chamados.anterior.diasMediana, periodo: MES_ATUAL(c), rotuloAnterior: MES_PASSADO(c) },
    metaSugerida: (v) => Math.max(1, Math.floor((v.atual ?? 3) * 0.8)),
  },
  // Orçamento de ferramentas (#6509): a meta do mês é o orçamento. O custo sai de
  // `custos_ferramentas`, que a aba CEO não carrega: lá fica sem valor.
  {
    id: 'tec_custo_ferramentas', departamento: 'tecnologia', nome: 'Custo de ferramentas (orçamento)', unidade: 'reais', sentido: 'menor',
    como_medir: 'Soma do custo mensal das ferramentas de Tecnologia e Marketing cadastradas na Visão Geral do Felipe. A meta de cada mês é o orçamento.',
    calcular: () => null,
    metaSugerida: () => 0,
    semValorNoPainel: true,
  },
];

export const kpiAutomatico = (id: string | null | undefined): DefinicaoKpiAuto | undefined =>
  id ? KPIS_AUTOMATICOS.find((k) => k.id === id) : undefined;

export const kpisAutomaticosDe = (departamento: string): DefinicaoKpiAuto[] =>
  KPIS_AUTOMATICOS.filter((k) => k.departamento === departamento);

/** Variação contra o período anterior, já no sentido do KPI: positivo = melhorou. */
export function melhoraDoKpi(v: Pick<ValorKpi, 'atual' | 'anterior'>, sentido: 'maior' | 'menor'): number | null {
  if (v.atual == null || v.anterior == null || v.anterior === 0) return null;
  const variacao = (v.atual - v.anterior) / Math.abs(v.anterior);
  return sentido === 'maior' ? variacao : -variacao;
}

/** Números de chamados de um mês, a partir das tarefas de solicitação para Tecnologia. */
export function numerosChamados(
  tarefas: { status: string | null; created_at: string; data_conclusao: string | null }[],
  ordMes: number,
): NumerosChamados {
  const [ini, fim] = janelaDoOrdinal(ordMes);
  const concluidas = tarefas.filter((t) => {
    const d = String(t.data_conclusao ?? '').slice(0, 10);
    return t.status === 'concluido' && d >= ini && d <= fim;
  });
  const dias = concluidas
    .map((t) => (Date.parse(t.data_conclusao!) - Date.parse(t.created_at)) / 86_400_000)
    .filter((d) => Number.isFinite(d) && d >= 0);
  // Dia da entrega no horário de Brasília (a conclusão vem em UTC).
  const diasComEntrega = new Set(concluidas.map((t) => new Date(Date.parse(t.data_conclusao!) - 3 * 3_600_000).toISOString().slice(0, 10)));
  const ordenados = [...dias].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  return {
    resolvidos: concluidas.length,
    diasMedios: dias.length ? dias.reduce((t, d) => t + d, 0) / dias.length : null,
    diasMediana: ordenados.length ? (ordenados.length % 2 ? ordenados[meio] : (ordenados[meio - 1] + ordenados[meio]) / 2) : null,
    noPrazo: dias.length ? dias.filter((d) => d <= PRAZO_CHAMADO_DIAS).length / dias.length : null,
    diasAtivos: diasComEntrega.size,
  };
}
